//@author: devin

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    effect,
    inject,
    input,
    model,
    output,
    ViewEncapsulation,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { InstanceCount } from "../../util/s25.instance.count";
import { S25ItemI } from "../../pojo/S25ItemI";
import { GenericDropdownUtil } from "../s25-dropdown/generic/generic.dropdown.util";

@TypeManagerDecorator("s25-ng-multiselect-results")
@Component({
    selector: "s25-ng-multiselect-results",
    host: {
        class: "s25-multiselect",
    },
    template: `
        @if (selectedItems().length > 0) {
            <div class="s25-multiselect-result">
                @if (showMatching()) {
                    <div class="s25-multiselect-popup-position-header">
                        <div class="s25-matching-container">
                            <label class="s25-label s25-label-all"
                                ><input
                                    type="radio"
                                    name="anyOrAll-{{ instanceId }}"
                                    [(ngModel)]="matching"
                                    value="any"
                                    class="s25-input"
                                />
                                Any</label
                            >
                            <label class="s25-label s25-label-all"
                                ><input
                                    type="radio"
                                    name="anyOrAll-{{ instanceId }}"
                                    [(ngModel)]="matching"
                                    value="all"
                                    class="s25-input"
                                />
                                All</label
                            >
                        </div>
                    </div>
                }
                <div class="s25-multiselect-columns-container">
                    <div
                        class="s25-multiselect-columns"
                        s25-ng-dnd-sortable
                        [sortable]="sortBy() === 'manual'"
                        [items]="selectedItems()"
                        (orderChanged)="onSortOrderChanged()"
                    >
                        @for (item of selectedItems(); track item?.itemId) {
                            <s25-ng-multiselect-item
                                s25-ng-dnd-sortable-item
                                [draggable]="sortBy() === 'manual'"
                                [index]="$index"
                                type="cross"
                                [hidden]="item.isSecret"
                                [item]="item"
                                (clear)="clear.emit()"
                                (select)="select.emit(item)"
                                (remove)="remove.emit(item)"
                                (changed)="changed.emit()"
                                [hasQuantity]="hasQuantity()"
                                [disabled]="disabled()"
                                class="c-tagItem ngInlineBlock"
                            />
                        }
                    </div>
                </div>
            </div>
        }
    `,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25MultiselectResultsComponent extends InstanceCount {
    readonly uuid = input<string | undefined>(undefined);
    readonly selectedItems = model.required<S25ItemI[]>();
    readonly disabled = input(false);
    readonly matching = model<"any" | "all">("any");
    readonly showMatching = input(false);
    readonly hasQuantity = input(false);
    readonly sortBy = input<"none" | "selection" | "manual">("none");
    readonly sortOrder = input<"asc" | "desc">("asc");

    readonly clear = output<void>();
    readonly select = output<S25ItemI>();
    readonly remove = output<S25ItemI>();
    readonly changed = output<void>();

    readonly cd = inject(ChangeDetectorRef);

    readonly sortedItems = computed(() => {
        const sortBy = this.sortBy();
        const items = [...this.selectedItems()];
        if (sortBy === "none") return items;
        const order = this.sortOrder();
        switch (sortBy) {
            case "selection":
            case "manual": {
                const insertionOrder = new Map(this.previousSortOrder.map((id, index) => [id, index]));
                return items.sort((a, b) => {
                    const indexA = insertionOrder.get(GenericDropdownUtil.getId(a)) ?? Number.MAX_SAFE_INTEGER;
                    const indexB = insertionOrder.get(GenericDropdownUtil.getId(b)) ?? Number.MAX_SAFE_INTEGER;
                    if (order === "asc" || sortBy === "manual") return indexA - indexB;
                    return indexB - indexA;
                });
            }
        }
        return items;
    });

    private previousSortOrder: (number | string)[] = [];

    constructor() {
        super();
        effect(() => {
            const sortedItems = this.sortedItems();
            const isSorted = this.selectedItems().every((item, index) => item === sortedItems[index]);
            this.previousSortOrder = sortedItems.map(GenericDropdownUtil.getId);
            if (isSorted) return;
            this.selectedItems.set(sortedItems);
        });
    }

    // Despite the signals this is still necessary as there are still places where the selected items array is mutated
    refresh = () => {
        try {
            this.cd.detectChanges();
        } catch (error: any) {}
    };

    onSortOrderChanged() {
        this.previousSortOrder = this.selectedItems().map(GenericDropdownUtil.getId);
        this.selectedItems.set([...this.selectedItems()]);
    }
}
